import { formatBreakEven, formatCount, formatMoney, formatPercent } from "@shared/roi/format";
import type { Projection } from "@shared/roi/types";
import { StatTile } from "@client/components/StatTile";

/**
 * The five figures the whole page exists to produce, recomputed in the browser
 * on every keystroke.
 *
 * Sticky, because these are what someone is watching while they drag a slider a
 * screen further down; a KPI strip that scrolls away turns every experiment into
 * a scroll up, a read, and a scroll back.
 */
export function KpiStrip({
  projection,
  currencyCode,
  horizonYears,
}: {
  projection: Projection;
  currencyCode: string;
  horizonYears: number;
}) {
  const money = (value: number, signed = false) =>
    formatMoney(currencyCode, value, { compact: true, signed });

  return (
    <div
      role="group"
      aria-label="Projection totals"
      // Stuck below the shell's own header rather than at `top-0`: the header
      // is sticky too and 60px tall, so a strip at zero is a strip nobody ever
      // sees again after the first scroll. The shell's background sits behind
      // it — a translucent strip over moving content is unreadable.
      className="bg-[var(--haze)]/95 sticky top-[var(--app-header-h)] z-20 -mx-1 grid gap-2 rounded-xl px-1 py-2 backdrop-blur sm:grid-cols-3 lg:grid-cols-6"
    >
      <StatTile
        label="ROI"
        value={formatPercent(projection.roiPct)}
        hint={projection.cumCost === 0 ? "Nothing spent yet" : `over ${horizonYears} years`}
      />
      <StatTile
        label="Break-even"
        value={formatBreakEven(projection.breakEvenYears, horizonYears)}
        hint={
          projection.npv === null
            ? undefined
            : `NPV ${formatMoney(currencyCode, projection.npv, { compact: true, signed: true })}`
        }
      />
      <StatTile label="Revenue" tone="revenue" value={money(projection.cumRevenue)} />
      <StatTile label="Cost" tone="cost" value={money(projection.cumCost)} />
      <StatTile
        label="Net"
        tone={projection.cumNet >= 0 ? "revenue" : "cost"}
        value={money(projection.cumNet, true)}
      />
      <StatTile
        label="ARR"
        tone={projection.endingArr >= 0 ? "revenue" : "cost"}
        value={money(projection.endingArr)}
        hint={`${formatCount(projection.endingActive)} active at the end`}
      />
    </div>
  );
}
